import React from 'react';
import { Clock, CheckCircle, XCircle, Bell, Ban } from 'lucide-react';

interface WaitlistBadgeProps {
  status: 'waiting' | 'notified' | 'converted' | 'expired' | 'cancelled';
  position?: number | null;
  compact?: boolean;
}

/**
 * WaitlistBadge - Small badge showing waitlist status and position
 * Used in vendor dashboard and fair cards
 */
function WaitlistBadge({ status, position, compact = false }: WaitlistBadgeProps) {
  const getBadgeStyles = () => {
    switch (status) {
      case 'waiting':
        return 'bg-moonbone text-primary-dark border-teal-200';
      case 'notified':
        return 'bg-teal-100 text-primary-dark border-teal-200';
      case 'converted':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'expired':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'cancelled':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getIcon = () => {
    switch (status) {
      case 'waiting':
        return <Clock className="w-3 h-3" />;
      case 'notified':
        return <Bell className="w-3 h-3" />;
      case 'converted':
        return <CheckCircle className="w-3 h-3" />;
      case 'expired':
        return <XCircle className="w-3 h-3" />;
      case 'cancelled':
        return <Ban className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const getLabel = () => {
    switch (status) {
      case 'waiting':
        return position ? `Waitlist #${position}` : 'Waitlisted';
      case 'notified':
        return 'Slot Available!';
      case 'converted':
        return 'Reserved';
      case 'expired':
        return 'Offer Expired';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getBadgeStyles()}`}>
        {getIcon()}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getBadgeStyles()}`}>
      {getIcon()}
      {getLabel()}
    </span>
  );
}

export default WaitlistBadge;
